/**
 * Dashboard Page
 *
 * Main dashboard landing page with metrics for all platform resources.
 * Features favorite applications quick launch section and quick actions.
 * Uses React Query hooks to avoid duplicate API requests.
 */

import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { useAuth } from "../contexts/AuthContext";
import { useLayoutProps } from "../hooks/useLayoutProps";
import { hasPermission } from "../lib/utils/permissions";
import { ROUTES } from "../lib/constants";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { StatCard } from "../components/ui/StatCard";
import { SectionCard } from "../components/ui/SectionCard";
import { useUsers } from "../features/users/hooks/useUsers";
import { useApplications } from "../features/applications/hooks/useApplications";
import { useVolumes } from "../features/volumes/hooks/useVolumes";
import { useSecrets } from "../features/secrets/hooks/useSecrets";
import { useDatabaseConnections } from "../features/database-connections/hooks/useDatabaseConnections";
import { useDocumentDatabases } from "../features/document-databases/hooks/useDocumentDatabases";
import { useManagedSqlDbs } from "../features/managed-sql-databases/hooks/useManagedSqlDbs";
import {
  RocketLaunchIcon,
  StarIcon,
  UsersIcon,
  CubeIcon,
  CircleStackIcon,
  KeyIcon,
  ServerStackIcon,
  DocumentIcon,
  PlusIcon,
  UserPlusIcon,
  CloudArrowUpIcon,
} from "@heroicons/react/24/solid";
import { FolderIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";

export const DashboardPage: React.FC = () => {
  const { user, tenant, activeRole } = useAuth();
  const layoutProps = useLayoutProps();
  const navigate = useNavigate();
  const tenantId = tenant?.tenantId || "";

  // Use React Query hooks to fetch data (with caching and deduplication)
  const { data: users, isLoading: usersLoading } = useUsers(tenantId);
  const { data: applications, isLoading: applicationsLoading } = useApplications(tenantId, { limit: 100 });
  const { data: volumes, isLoading: volumesLoading } = useVolumes(tenantId);
  const { data: secrets, isLoading: secretsLoading } = useSecrets(tenantId);
  const { data: databaseConnections, isLoading: dbConnectionsLoading } = useDatabaseConnections(tenantId);
  const { data: documentDatabases, isLoading: docDbsLoading } = useDocumentDatabases(tenantId);
  const { data: managedSqlDbs, isLoading: managedSqlLoading } = useManagedSqlDbs(tenantId);

  // Get favorite applications
  const favoriteApps = useMemo(() => {
    if (!applications || !user?.favoriteApplications) return [];
    const favoriteIds = new Set(user.favoriteApplications);
    return applications.filter((app) => favoriteIds.has(app.applicationId)).slice(0, 12);
  }, [applications, user?.favoriteApplications]);

  // Compute metrics from React Query data
  const metrics = useMemo(
    () => ({
      usersCount: users?.length || 0,
      applicationsCount: applications?.length || 0,
      volumesCount: volumes?.length || 0,
      secretsCount: secrets?.length || 0,
      dbConnectionsCount: databaseConnections?.length || 0,
      documentDbsCount: documentDatabases?.length || 0,
      managedSqlDbsCount: managedSqlDbs?.databases?.length || 0,
    }),
    [users, applications, volumes, secrets, databaseConnections, documentDatabases, managedSqlDbs]
  );

  const loading =
    usersLoading || applicationsLoading || volumesLoading || secretsLoading || dbConnectionsLoading || docDbsLoading || managedSqlLoading;

  // Resource cards configuration
  const resourceCards = [
    { label: "Users", count: metrics.usersCount, icon: UsersIcon, href: "/app/users", iconBg: "bg-primary-100 dark:bg-primary-900/30", iconColor: "text-primary-600 dark:text-primary-400" },
    { label: "Applications", count: metrics.applicationsCount, icon: CubeIcon, href: "/app/applications", iconBg: "bg-primary-100 dark:bg-primary-900/30", iconColor: "text-primary-600 dark:text-primary-400" },
    { label: "Volumes", count: metrics.volumesCount, icon: FolderIcon, href: "/app/volumes", iconBg: "bg-violet-100 dark:bg-violet-500/20", iconColor: "text-violet-600 dark:text-violet-400" },
    { label: "Secrets", count: metrics.secretsCount, icon: KeyIcon, href: "/app/secrets", iconBg: "bg-amber-100 dark:bg-amber-900/30", iconColor: "text-amber-600 dark:text-amber-400" },
    { label: "DB Connections", count: metrics.dbConnectionsCount, icon: ServerStackIcon, href: "/app/database-connections", iconBg: "bg-accent-100 dark:bg-accent-700/20", iconColor: "text-accent-600 dark:text-accent-400" },
    { label: "Document DBs", count: metrics.documentDbsCount, icon: DocumentIcon, href: "/app/document-databases", iconBg: "bg-primary-100 dark:bg-primary-900/30", iconColor: "text-primary-600 dark:text-primary-400" },
    { label: "Managed SQL", count: metrics.managedSqlDbsCount, icon: CircleStackIcon, href: "/app/managed-sql-databases", iconBg: "bg-rose-100 dark:bg-rose-900/30", iconColor: "text-rose-600 dark:text-rose-400" },
  ];

  // Quick actions configuration
  const quickActions = [
    { label: "Create Application", icon: PlusIcon, href: "/app/applications", action: "create-app" },
    { label: "Invite User", icon: UserPlusIcon, href: "/app/users", action: "invite-user", requiresPermission: "iam:CreateUser" },
    { label: "Deploy App", icon: CloudArrowUpIcon, href: "/app/applications", action: "deploy" },
    { label: "Launch App", icon: RocketLaunchIcon, href: "/app/launcher", action: "launch" },
  ].filter((action) => !action.requiresPermission || hasPermission(activeRole?.permissions, action.requiresPermission));

  const handleLaunchApp = (appId: string, e?: React.MouseEvent) => {
    const launchUrl = `${ROUTES.LAUNCH}/${appId}`;
    if (e && (e.ctrlKey || e.metaKey)) {
      window.open(launchUrl, '_blank', 'noopener,noreferrer');
    } else {
      navigate(launchUrl);
    }
  };

  const handleLaunchAppNewTab = (appId: string) => {
    window.open(`${ROUTES.LAUNCH}/${appId}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Welcome Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-heading-2xl" style={{ color: 'var(--color-text-primary)' }}>Welcome back, {user?.firstName}!</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Organization: <span className="font-medium">{tenant?.tenantCode}</span>
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <Card>
          <Card.Body className="py-4">
            <div className="flex flex-wrap gap-3">
              {quickActions.map((action) => (
                <Button key={action.action} variant="secondary" size="sm" onClick={() => navigate(action.href)} className="flex items-center gap-2">
                  <action.icon className="h-4 w-4" />
                  {action.label}
                </Button>
              ))}
            </div>
          </Card.Body>
        </Card>

        {/* Favorite Applications Section */}
        {favoriteApps.length > 0 && (
          <SectionCard
            title="Favorite Applications"
            icon={<StarIcon className="h-5 w-5 text-yellow-500" />}
            headerActions={
              <Button variant="ghost" size="sm" onClick={() => navigate("/app/launcher")}>
                View all
              </Button>
            }
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {favoriteApps.map((app) => (
                <div
                  key={app.applicationId}
                  className="card rounded-lg p-4 hover:shadow-card-hover transition-shadow"
                  style={{ border: '1px solid var(--color-border)' }}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center h-10 w-10 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex-shrink-0">
                      <RocketLaunchIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold truncate" style={{ color: 'var(--color-text-primary)' }}>{app.applicationName}</h4>
                      {app.description && (
                        <p className="text-xs line-clamp-2 mt-1" style={{ color: 'var(--color-text-secondary)' }}>{app.description}</p>
                      )}
                      <div className="flex gap-2 mt-3">
                        <Button
                          variant="primary"
                          size="sm"
                          className="flex-1"
                          onClick={(e) => handleLaunchApp(app.applicationId, e)}
                          disabled={!app.isActive}
                        >
                          <RocketLaunchIcon className="h-3 w-3 mr-1" />
                          Launch
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleLaunchAppNewTab(app.applicationId)}
                          disabled={!app.isActive}
                          title="Open in new tab"
                        >
                          <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* Resource Metrics Grid */}
        <div>
          <h2 className="text-heading-lg mb-4" style={{ color: 'var(--color-text-primary)' }}>Platform Resources</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
            {resourceCards.map((resource, index) => {
              const IconComponent = resource.icon;
              return (
                <StatCard
                  key={resource.label}
                  title={resource.label}
                  value={resource.count}
                  icon={<IconComponent className="h-5 w-5" />}
                  iconBg={resource.iconBg}
                  iconColor={resource.iconColor}
                  isLoading={loading}
                  index={index}
                  className="cursor-pointer hover:shadow-card-hover transition-shadow"
                  onClick={() => navigate(resource.href)}
                />
              );
            })}
          </div>
        </div>

        {/* Getting Started Card */}
        <SectionCard title="Getting Started">
          <div className="prose prose-sm max-w-none">
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Welcome to Ketrics! This is your main dashboard where you can manage your organization's resources and applications.
            </p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                  <UserPlusIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Invite Team Members</p>
                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Add users to your organization</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                  <PlusIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Create Applications</p>
                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Build and deploy custom apps</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-violet-100 dark:bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                  <CircleStackIcon className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Set Up Resources</p>
                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Configure volumes and databases</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-accent-100 dark:bg-accent-700/20 flex items-center justify-center flex-shrink-0">
                  <RocketLaunchIcon className="h-4 w-4 text-accent-600 dark:text-accent-400" />
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>Launch Applications</p>
                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Run your deployed applications</p>
                </div>
              </div>
            </div>
            <p className="mt-4 text-xs" style={{ color: 'var(--color-text-muted)' }}>
              Tip: Star your frequently-used applications in the Launcher to see them here for quick access!
            </p>
          </div>
        </SectionCard>
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
